// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Upload dialog UI: status/error display, file selection summary,
// aircraft-mode toggle, aircraft <select> population, and dialog
// open/close. The upload mechanics themselves (selectedSubmit handler,
// metadata params, ingest poll) stay in app.js — this module is purely
// the dialog presentation surface.
//
// Exposed as a factory because several deps live in app.js (read-only
// preview/action messages, aircraft option lists, the database-source
// flag) and we want late binding against them.

import {
  uploadCapacityCounterText,
  uploadCapacitySelectionModel,
  uploadFileDisplayName,
  uploadSelectionFiles,
  uploadSelectionSummary,
} from "./aircraft-upload-model.js";
import { aircraftDisplayLabel } from "./aircraft-profile-model.js";

export function createUploadDialog({
  escapeHtml,
  populateMonitorSelects,
  aircraftOptionsForUpload,
  preferredUploadAircraft,
  appReadOnlyMode,
  readOnlyPreviewMessage,
  syncReadOnlyUi,
  setUploadDialogPostSuccessPanel = () => {},
  aircraftDatabaseIdForUpload = () => "",
  newUiFeatureEnabled = () => false,
  aircraftDisplayLabel = (value) => String(value ?? ""),
}) {
  function setUploadDialogError(message = "") {
    const error = document.querySelector("#uploadDialogError");
    if (!error) return;
    error.hidden = !message;
    error.textContent = message;
  }

  function setUploadDialogStatus(message = "") {
    const status = document.querySelector("#uploadDialogStatus");
    if (!status) return;
    status.hidden = !message;
    status.textContent = message;
  }

  function selectedUploadFiles() {
    const fileInput = document.querySelector("#uploadFileInput");
    const folderInput = document.querySelector("#uploadFolderInput");
    return uploadSelectionFiles([fileInput?.files, folderInput?.files]);
  }

  function updateUploadSelectionSummary() {
    const summary = document.querySelector("#uploadSelectionSummary");
    if (!summary) return;
    const fileInput = document.querySelector("#uploadFileInput");
    const folderInput = document.querySelector("#uploadFolderInput");
    const selectedCount = Number(fileInput?.files?.length ?? 0) + Number(folderInput?.files?.length ?? 0);
    const supportedFiles = selectedUploadFiles();
    if (!selectedCount) {
      summary.textContent = "Supported source files: `.ald`, `.csv`, `.jpi`, and `.zip` (EI CGR batches). 75 MB max per file.";
    } else {
      const folderSelected = [...(folderInput?.files ?? [])].length > 0;
      summary.textContent = uploadSelectionSummary({
        selectedCount,
        supportedCount: supportedFiles.length,
        folderSelected,
      });
    }
    // Driven from here rather than from each caller: this function is already
    // invoked from every path that changes the selection (open, the file-input
    // change listener in setup-navigation, and drops), so the list cannot fall
    // out of step with the summary above it.
    renderSelectedFileList();
  }

  // Human-readable size for the selected-file list. Kept local and simple: this
  // is the only place a raw byte count is shown to the user in this dialog.
  function uploadFileSizeLabel(bytes) {
    const size = Number(bytes ?? 0);
    if (!Number.isFinite(size) || size <= 0) return "";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Render the scrollable list of files queued for upload. Driven from the same
   * inputs as the summary, so it stays correct whichever way files arrived —
   * Choose Files, Choose Folder, or a drop.
   *
   * Lists only the SUPPORTED files, matching what will actually be sent; the
   * summary line above already reports how many of the selection were skipped,
   * so an unsupported file is accounted for rather than silently missing.
   */
  function renderSelectedFileList() {
    const list = document.querySelector("#uploadSelectedFiles");
    if (!list) return;
    const files = selectedUploadFiles();
    list.hidden = files.length === 0;
    if (!files.length) {
      list.replaceChildren();
      return;
    }
    const fragment = document.createDocumentFragment();
    for (const file of files) {
      const item = document.createElement("li");
      item.className = "upload-selected-file";
      const name = document.createElement("span");
      name.className = "upload-selected-file-name";
      const displayName = uploadFileDisplayName(file);
      name.textContent = displayName;
      // Folder picks carry a relative path; the full path is worth keeping on
      // hover because two folders can hold identically named logs.
      name.title = file.webkitRelativePath || displayName;
      const size = document.createElement("span");
      size.className = "upload-selected-file-size";
      size.textContent = uploadFileSizeLabel(file.size);
      item.append(name, size);
      fragment.append(item);
    }
    list.replaceChildren(fragment);
  }

  function syncUploadAircraftMode() {
    const select = document.querySelector("#uploadAircraftSelect");
    const newField = document.querySelector("#uploadAircraftNewField");
    const newInput = document.querySelector("#uploadAircraftNewInput");
    const metadataFields = document.querySelector("#uploadAircraftMetadataFields");
    const creatingNew = select?.value === "__new__";
    if (newField) newField.hidden = !creatingNew;
    if (metadataFields) {
      metadataFields.hidden = !creatingNew;
      metadataFields.querySelectorAll("input, select").forEach((input) => {
        input.disabled = metadataFields.hidden;
        if (metadataFields.hidden) input.value = "";
      });
      if (!metadataFields.hidden) populateMonitorSelects(
        document.querySelector("#uploadAircraftMonitorMakeInput"),
        document.querySelector("#uploadAircraftMonitorModelInput"),
      );
    }
    if (newInput) {
      newInput.required = Boolean(creatingNew);
      if (creatingNew) {
        window.setTimeout(() => newInput.focus(), 0);
      } else {
        newInput.value = "";
      }
    }
  }

  function populateUploadAircraftSelect(preferredAircraft = preferredUploadAircraft()) {
    const select = document.querySelector("#uploadAircraftSelect");
    if (!select) return;
    const options = aircraftOptionsForUpload();
    select.innerHTML = [
      ...options.map((aircraft) => `<option value="${escapeHtml(aircraft)}" data-aircraft-id="${escapeHtml(aircraftDatabaseIdForUpload(aircraft) ?? "")}">${escapeHtml(aircraftDisplayLabel(aircraft))}</option>`),
      '<option value="__new__">Add new aircraft...</option>',
    ].join("");
    if (preferredAircraft && options.includes(preferredAircraft)) {
      select.value = preferredAircraft;
    } else if (options.length) {
      select.value = options[0];
    } else {
      select.value = "__new__";
    }
    syncUploadAircraftMode();
  }

  function openUploadDialog(preferredAircraft = preferredUploadAircraft(), options = {}) {
    const dialog = document.querySelector("#uploadDialog");
    const form = document.querySelector("#uploadDialogForm");
    const fileInput = document.querySelector("#uploadFileInput");
    const folderInput = document.querySelector("#uploadFolderInput");
    const chooseFilesButton = document.querySelector("#uploadChooseFilesButton");
    if (!dialog || !form || !fileInput || !folderInput) return;
    setUploadDialogPostSuccessPanel(options.postSuccessPanel ?? "");
    form.reset();
    fileInput.value = "";
    folderInput.value = "";
    setUploadDialogError("");
    setUploadDialogStatus(appReadOnlyMode() ? readOnlyPreviewMessage() : "");
    updateUploadSelectionSummary();
    populateUploadAircraftSelect(preferredAircraft);
    syncUploadDialogNewUiPresentation();
    syncReadOnlyUi();
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
    window.setTimeout(() => chooseFilesButton?.focus(), 0);
  }

  // New UI: the sidebar owns aircraft switching, so the dialog states its
  // target instead of re-asking. The picker only appears for the
  // create-new-aircraft flow (first upload). Old UI keeps the original copy.
  function syncUploadDialogNewUiPresentation() {
    const dialog = document.querySelector("#uploadDialog");
    const title = document.querySelector("#uploadDialogTitle");
    if (!dialog || !title) return;
    const eyebrow = dialog.querySelector(".eyebrow");
    const copy = dialog.querySelector(".upload-dialog-copy");
    const aircraftField = document.querySelector("#uploadAircraftField");
    const note = document.querySelector("#uploadDialogAircraftNote");
    const select = document.querySelector("#uploadAircraftSelect");
    const newUi = newUiFeatureEnabled();
    const targetValue = String(select?.value ?? "");
    const targetIsExisting = Boolean(targetValue && targetValue !== "__new__");
    title.textContent = newUi ? "Upload Logs" : "Choose the aircraft for this upload";
    if (eyebrow) eyebrow.hidden = newUi;
    if (copy) copy.hidden = newUi;
    // Single owner of this field's visibility. Hidden when the New UI already
    // knows the target aircraft, OR when the fleet is empty and the only option
    // is "Add new aircraft..." — a picker with nothing to pick.
    // Setting it here and in populateUploadAircraftSelect meant this ran last
    // and silently undid the empty-fleet hide.
    const hasAircraftOptions = aircraftOptionsForUpload().length > 0;
    if (aircraftField) aircraftField.hidden = !hasAircraftOptions || (newUi && targetIsExisting);
    if (note) {
      const showNote = newUi && targetIsExisting;
      note.hidden = !showNote;
      note.textContent = showNote
        ? `Uploading to ${aircraftDisplayLabel(targetValue) || targetValue}. To upload for a different aircraft, pick it in the sidebar first.`
        : "";
    }
  }

  function closeUploadDialog() {
    const dialog = document.querySelector("#uploadDialog");
    if (!dialog) return;
    setUploadDialogError("");
    setUploadDialogStatus("");
    if (typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
    }
  }

  function uploadAircraftTargetValue() {
    const select = document.querySelector("#uploadAircraftSelect");
    const newInput = document.querySelector("#uploadAircraftNewInput");
    if (select?.value === "__new__") return String(newInput?.value ?? "").trim().toUpperCase();
    return String(select?.value ?? "").trim().toUpperCase();
  }

  function uploadAircraftTargetDatabaseId() {
    const select = document.querySelector("#uploadAircraftSelect");
    if (!select || select.value === "__new__") return "";
    return String(select.selectedOptions?.[0]?.dataset?.aircraftId ?? "").trim();
  }

  // Over-capacity picker: the selection holds more flights than the plan has
  // room for, so the member chooses which ones to upload. The newest flights
  // come pre-checked (matching which flights the plan keeps); checking is
  // hard-capped at the remaining room. Resolves the chosen File[] on confirm,
  // null on cancel/close.
  function chooseUploadFilesWithinCapacity(files, capacity = {}) {
    const dialog = document.querySelector("#uploadCapacityDialog");
    const form = document.querySelector("#uploadCapacityDialogForm");
    const list = document.querySelector("#uploadCapacityDialogList");
    const counter = document.querySelector("#uploadCapacityDialogCounter");
    const confirmButton = document.querySelector("#uploadCapacityDialogConfirm");
    if (!(dialog instanceof HTMLDialogElement) || !form || !list) return Promise.resolve(null);
    const model = uploadCapacitySelectionModel(files, capacity.remaining);
    if (!model.remaining) return Promise.resolve(null);
    list.replaceChildren(...model.files.map((file, index) => {
      const item = document.createElement("li");
      const label = document.createElement("label");
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.dataset.fileIndex = String(index);
      checkbox.checked = model.preChecked.has(index);
      const name = document.createElement("span");
      name.textContent = uploadFileDisplayName(file);
      label.append(checkbox, name);
      item.append(label);
      return item;
    }));
    const checkboxes = () => [...list.querySelectorAll('input[type="checkbox"]')];
    const syncCapacityState = () => {
      const checked = checkboxes().filter((box) => box.checked);
      const atCap = checked.length >= model.remaining;
      checkboxes().forEach((box) => {
        const frozen = atCap && !box.checked;
        box.disabled = frozen;
        box.closest("label")?.classList.toggle("is-disabled", frozen);
      });
      if (counter) counter.textContent = uploadCapacityCounterText(checked.length, model.files.length, model.remaining);
      if (confirmButton) confirmButton.disabled = checked.length === 0;
    };
    syncCapacityState();
    return new Promise((resolve) => {
      let chosen = null;
      const controller = new AbortController();
      const { signal } = controller;
      list.addEventListener("change", syncCapacityState, { signal });
      form.addEventListener("submit", () => {
        chosen = checkboxes()
          .filter((box) => box.checked)
          .map((box) => model.files[Number(box.dataset.fileIndex)])
          .filter(Boolean);
      }, { signal });
      document.querySelector("#uploadCapacityDialogCancel")?.addEventListener("click", () => dialog.close(), { signal });
      document.querySelector("#uploadCapacityDialogClose")?.addEventListener("click", () => dialog.close(), { signal });
      dialog.addEventListener("close", () => {
        controller.abort();
        resolve(chosen);
      }, { signal });
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
    });
  }

  return {
    setUploadDialogError,
    setUploadDialogStatus,
    selectedUploadFiles,
    updateUploadSelectionSummary,
    syncUploadAircraftMode,
    populateUploadAircraftSelect,
    openUploadDialog,
    closeUploadDialog,
    uploadAircraftTargetValue,
    uploadAircraftTargetDatabaseId,
    chooseUploadFilesWithinCapacity,
  };
}
